import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface UploadConflictDialogProps {
  /** Conflicting target paths; the dialog is shown while this is non-empty. */
  conflicts: string[]
  /** Proceed and overwrite the existing documents. */
  onReplace: () => void
  /** Abort the whole batch — nothing is uploaded. */
  onCancel: () => void
}

/**
 * Pre-batch "these files already exist" prompt shared by the case-document and
 * data-lake uploaders. The user either replaces every colliding document or
 * cancels the entire upload (SP-357).
 */
export function UploadConflictDialog({
  conflicts,
  onReplace,
  onCancel,
}: UploadConflictDialogProps) {
  const { t } = useTranslation()
  const count = conflicts.length

  return (
    <AlertDialog
      open={count > 0}
      onOpenChange={open => {
        if (!open) onCancel()
      }}
    >
      {/* The uploader modal is a Dialog (overlay z-200 / content z-210); the
          default AlertDialog sits at z-50 and would render *behind* it. Lift
          this prompt above the upload modal — same convention as
          ExtractionScopeModal's confirm dialog. */}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('cases.documents.conflict.title', { count })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('cases.documents.conflict.body', { count })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/50 p-2">
          <ul className="space-y-1">
            {conflicts.map(path => (
              // Phone: a path wraps so the file name at its end stays readable.
              <li
                key={path}
                className="truncate font-mono text-xs text-muted-foreground max-md:whitespace-normal max-md:break-all"
              >
                {path}
              </li>
            ))}
          </ul>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {t('cases.documents.conflict.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onReplace}>
            {t('cases.documents.conflict.replace')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
